import React from "react";
import { Check, Star, MessageCircle, Mail, Download, Apple } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { trackEvent } from "../lib/analytics";

const PLAY_URL = "https://play.google.com/store/apps/details?id=fi.duunari.app";
const APPSTORE_URL = "https://apps.apple.com/app/duunari-app/id6779275969";
const MAILTO = "mailto:duunari.online@gmail.com";

export const Pricing = () => {
  const { t, tRaw } = useLanguage();

  const priceOf = (key, fallback) => {
    // Keep prices identical across languages — sourced from constants.
    return fallback;
  };

  const TIERS = [
    {
      id: "perussetti",
      tierKey: "tuntilappu",
      price: "0 €",
      period: "",
      labelKey: "employee",
      href: PLAY_URL,
      highlight: false,
      freeBadge: true,
    },
    {
      id: "pro",
      tierKey: "pro",
      price: "9,90 €",
      period: t("pricing.per_month"),
      labelKey: "employee",
      href: PLAY_URL,
      highlight: true,
    },
    {
      id: "pomo-s",
      tierKey: "pomo_s",
      price: "49 €",
      period: t("pricing.per_month"),
      labelKey: "boss",
      href: PLAY_URL,
      highlight: false,
    },
    {
      id: "pomo-l",
      tierKey: "pomo_l",
      price: "129 €",
      period: t("pricing.per_month"),
      labelKey: "boss",
      href: PLAY_URL,
      highlight: false,
    },
    {
      id: "pomo-projekti",
      tierKey: "pomo_projekti",
      price: "49,90 €",
      period: t("pricing.per_month_per_project"),
      labelKey: "company",
      href: PLAY_URL,
      highlight: false,
    },
  ];

  return (
    <section
      id="hinnasto"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 border-t-2 border-[#1a1a1a] grain"
    >
      <div className="absolute top-8 left-5 md:left-10 font-mono text-[10px] tracking-[0.3em] text-[#A3A3A3] uppercase">
        {t("pricing.section_number")}
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="text-center mb-16">
          <button
            type="button"
            data-testid="pricing-launch-banner"
            onClick={() => {
              const el = document.getElementById("pricing-pomo-projekti");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
              trackEvent("Click: Launch Banner");
            }}
            className="group inline-flex items-center gap-2 mb-6 px-4 py-2 border-2 border-[#FFEA00] bg-[#FFEA00]/10 hover:bg-[#FFEA00] hover:text-black text-[#FFEA00] transition-colors"
          >
            <span aria-hidden="true">🎉</span>
            <span className="font-display font-bold text-xs md:text-sm tracking-[0.2em] uppercase">
              {t("pricing.launch_banner")}
            </span>
            <span className="font-display font-bold text-xs md:text-sm tracking-[0.2em] uppercase transition-transform group-hover:translate-x-1">→</span>
          </button>
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#FFEA00] uppercase mb-4">
            {t("pricing.eyebrow")}
          </div>
          <h2
            data-testid="pricing-heading"
            className="font-display font-bold text-white text-4xl md:text-6xl lg:text-7xl uppercase leading-none tracking-tight"
          >
            {t("pricing.heading_line1")}
            <br />
            <span className="text-[#FFEA00]">{t("pricing.heading_line2_accent")}</span>
            {t("pricing.heading_line2_suffix")}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-[#A3A3A3] text-base md:text-lg">
            {t("pricing.subline")}
          </p>
        </div>

        {/* Pricing grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {TIERS.map((tier) => {
            const features = tRaw(`pricing.tiers.${tier.tierKey}.features`) || [];
            return (
              <PriceCard
                key={tier.id}
                id={tier.id}
                name={t(`pricing.tiers.${tier.tierKey}.name`)}
                description={t(`pricing.tiers.${tier.tierKey}.description`)}
                price={tier.price}
                period={tier.period}
                features={Array.isArray(features) ? features : []}
                cta={t(`pricing.tiers.${tier.tierKey}.cta`)}
                href={tier.href}
                highlight={tier.highlight}
                freeBadge={tier.freeBadge}
                contactLinks={tier.contactLinks}
                labelText={t(`pricing.labels.${tier.labelKey}`)}
                badge={tier.highlight ? t("pricing.most_popular") : undefined}
                freeBadgeText={t("pricing.free_badge")}
                comingSoon={tier.comingSoon}
                comingSoonBadge={tier.comingSoonBadgeKey ? t(tier.comingSoonBadgeKey) : undefined}
                addonBadge={tier.tierKey === "pomo_projekti" ? t(`pricing.tiers.${tier.tierKey}.addon_label`) : undefined}
                subNote={tier.subNoteKey ? t(tier.subNoteKey) : undefined}
              />
            );
          })}
        </div>

        {/* Compare Table (PRO vs POMO S vs POMO L vs POMO PROJEKTI) */}
        <div data-testid="compare-table" className="mt-14">
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#FFEA00] uppercase mb-4 text-center">
            {t("pricing.compare.title")}
          </div>
          <div className="overflow-x-auto border-2 border-[#1f1f1f] bg-[#0a0a0a]">
            <table className="w-full text-left border-collapse min-w-[720px]">
              <thead>
                <tr className="border-b-2 border-[#1f1f1f]">
                  <th className="px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
                    {t("pricing.compare.feature_col")}
                  </th>
                  {["pro", "pomo_s", "pomo_l", "pomo_projekti"].map((tk) => (
                    <th
                      key={tk}
                      data-testid={`compare-header-${tk}`}
                      className={`relative px-4 py-3 font-display font-bold text-xs md:text-sm tracking-[0.15em] uppercase text-center ${tk === "pomo_l" ? "text-black bg-[#FFEA00]" : "text-white"}`}
                    >
                      {tk === "pomo_l" && (
                        <span
                          data-testid="compare-recommended-ribbon"
                          className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black text-[#FFEA00] font-display font-bold text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 border-2 border-[#FFEA00] whitespace-nowrap"
                        >
                          ★ {t("pricing.most_popular")}
                        </span>
                      )}
                      {t(`pricing.tiers.${tk}.name`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(tRaw("pricing.compare.rows") || []).map((row, i) => (
                  <tr key={i} className="border-b border-[#1a1a1a] last:border-b-0 hover:bg-[#111]">
                    <td className="px-4 py-3 text-sm text-[#D4D4D4] font-mono tracking-wide">{row.label}</td>
                    {row.values.map((v, j) => (
                      <td
                        key={j}
                        className={`px-4 py-3 text-center text-sm ${
                          j === 3 ? "text-[#FFEA00] font-bold" : j === 2 ? "text-white bg-[#FFEA00]/[0.08] border-l border-r border-[#FFEA00]/30" : "text-white"
                        }`}
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Enterprise / Custom bar (full-width) */}
        <div
          data-testid="enterprise-bar"
          className="relative mt-10 border-2 border-[#FFEA00] overflow-hidden"
        >
          <div
            className="absolute top-0 left-0 right-0 h-3"
            style={{ backgroundImage: "repeating-linear-gradient(45deg, #FFEA00 0 14px, #1a1a1a 14px 28px)" }}
          />
          <div
            className="absolute bottom-0 left-0 right-0 h-3"
            style={{ backgroundImage: "repeating-linear-gradient(45deg, #FFEA00 0 14px, #1a1a1a 14px 28px)" }}
          />
          <div className="relative py-10 md:py-12 px-6 md:px-12 bg-[#0F0F0F] flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#FFEA00] uppercase mb-3">
                {t("pricing.enterprise.eyebrow")}
              </div>
              <h3
                data-testid="enterprise-title"
                className="font-display font-bold text-white text-2xl md:text-3xl lg:text-4xl uppercase tracking-tight leading-tight"
              >
                {t("pricing.enterprise.title")}
              </h3>
              <p className="mt-3 text-sm md:text-base text-[#A3A3A3] leading-relaxed">
                {t("pricing.enterprise.subtitle")}
              </p>
              <p className="mt-3 font-mono text-xs tracking-[0.2em] text-[#FFEA00] uppercase">
                {t("pricing.enterprise.cta_hint")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a
                href="https://wa.me/358405311186"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enterprise-whatsapp"
                onClick={() => trackEvent("Click: WhatsApp (Enterprise)")}
                className="btn-hazard relative inline-flex items-center justify-center gap-2 px-6 py-3 font-display font-bold text-xs tracking-widest uppercase border-2 border-[#FFEA00] bg-[#FFEA00] text-black transition-colors hover:bg-transparent hover:text-[#FFEA00]"
              >
                <MessageCircle className="w-4 h-4 stroke-[2.5]" />
                <span>{t("pricing.contact_links.whatsapp")}</span>
              </a>
              <a
                href={MAILTO}
                data-testid="enterprise-email"
                onClick={() => trackEvent("Click: Email (Enterprise)")}
                className="btn-hazard relative inline-flex items-center justify-center gap-2 px-6 py-3 font-display font-bold text-xs tracking-widest uppercase border-2 border-[#FFEA00] bg-transparent text-[#FFEA00] transition-colors hover:bg-[#FFEA00] hover:text-black"
              >
                <Mail className="w-4 h-4 stroke-[2.5]" />
                <span>{t("pricing.contact_links.email")}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-12 max-w-3xl mx-auto text-center">
          <p className="font-mono text-[11px] tracking-[0.2em] text-[#A3A3A3] uppercase">
            {t("pricing.vat_note")}
          </p>
        </div>
      </div>
    </section>
  );
};

const PriceCard = ({
  id,
  name,
  price,
  period,
  description,
  features,
  highlight,
  badge,
  comingSoon,
  comingSoonBadge,
  addonBadge,
  freeBadge,
  freeBadgeText,
  contactLinks,
  labelText,
  subNote,
}) => {
  return (
    <article
      id={`pricing-${id}`}
      data-testid={`price-card-${id}`}
      className={`relative flex flex-col p-6 border-2 transition-all min-h-[460px] ${
        comingSoon
          ? "bg-[#0a0a0a] border-[#FFEA00]/50 hover:border-[#FFEA00]"
          : highlight
          ? "bg-[#0F0F0F] border-[#FFEA00] xl:scale-[1.02] z-10 shadow-[0_0_60px_-20px_rgba(255,234,0,0.4)]"
          : "bg-[#0F0F0F] border-[#222] hover:border-[#FFEA00]/60"
      }`}
    >
      {highlight && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FFEA00] text-black font-sans font-bold text-xs sm:text-sm tracking-[0.18em] uppercase px-4 py-1.5 flex items-center gap-2 z-10 shadow-[3px_3px_0_0_rgba(0,0,0,0.6)]">
          <Star className="w-3.5 h-3.5 fill-black" />
          {badge}
        </div>
      )}

      {freeBadge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FFEA00] text-black font-sans font-bold text-xs sm:text-sm tracking-[0.2em] uppercase px-4 py-1.5 z-10 shadow-[3px_3px_0_0_rgba(0,0,0,0.6)]">
          {freeBadgeText}
        </div>
      )}

      {comingSoon && comingSoonBadge && (
        <div
          data-testid={`price-coming-soon-${id}`}
          className="absolute -top-4 left-1/2 -translate-x-1/2 bg-black text-[#FFEA00] font-sans font-bold text-xs sm:text-sm tracking-[0.2em] uppercase px-4 py-1.5 z-10 border-2 border-[#FFEA00] shadow-[3px_3px_0_0_rgba(0,0,0,0.6)]"
        >
          {comingSoonBadge}
        </div>
      )}

      {/* Label (Worker / Boss / Company) */}
      <div className="relative flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
          {labelText}
        </span>
        {addonBadge && (
          <span
            data-testid={`price-addon-badge-${id}`}
            className="inline-block px-1.5 py-0.5 border border-[#FFEA00] bg-[#FFEA00]/10 text-[#FFEA00] font-display font-bold text-[9px] tracking-[0.2em] uppercase"
          >
            {addonBadge}
          </span>
        )}
      </div>
      <h3 className="relative mt-2 font-display font-bold text-2xl uppercase leading-tight tracking-tight text-white">
        {name}
      </h3>
      <p className="relative mt-2 text-xs leading-relaxed min-h-[36px] text-[#A3A3A3]">
        {description}
      </p>

      {/* Price */}
      <div className="relative mt-6 pb-5 border-b-2 border-[#1f1f1f]">
        <div className="flex items-baseline gap-1.5">
          <span
            className={`font-display font-bold leading-none tracking-tight ${
              highlight || freeBadge || comingSoon ? "text-[#FFEA00]" : "text-white"
            } text-4xl`}
          >
            {price}
          </span>
          <span className="font-mono text-xs text-[#A3A3A3]">{period}</span>
        </div>
        {subNote && (
          <p
            data-testid={`price-subnote-${id}`}
            className="mt-3 font-mono text-[11px] leading-relaxed text-[#A3A3A3]"
          >
            {subNote}
          </p>
        )}
      </div>

      {/* Features */}
      <ul className="relative mt-5 space-y-2.5 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#D4D4D4]">
            <Check
              className={`w-4 h-4 mt-0.5 shrink-0 ${
                highlight ? "text-[#FFEA00]" : "text-[#FFEA00]/70"
              }`}
              strokeWidth={3}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {contactLinks ? (
        <div className="mt-6 flex flex-col gap-2.5" data-testid={`price-contact-links-${id}`}>
          {contactLinks.map(({ label, href: chref, Icon, external, testKey }) => (
            <a
              key={testKey || label}
              href={chref}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
              data-testid={`price-contact-${id}-${testKey || "link"}`}
              onClick={() => {
                if (testKey === "whatsapp") trackEvent("Click: WhatsApp");
                else if (testKey === "email") trackEvent("Click: Email");
              }}
              className="btn-hazard relative inline-flex items-center justify-center gap-2 px-4 py-3 font-display font-bold text-xs tracking-widest uppercase border-2 border-[#FFEA00] bg-transparent text-[#FFEA00] transition-colors"
            >
              <Icon className="w-4 h-4 stroke-[2.5]" />
              <span>{label}</span>
            </a>
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-2.5" data-testid={`price-store-buttons-${id}`}>
          <a
            href={PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`price-store-${id}-android`}
            onClick={() => trackEvent("Click: Google Play")}
            className={`btn-hazard relative inline-flex items-center justify-center gap-2 px-4 py-2.5 font-display font-bold text-xs tracking-widest uppercase border-2 transition-colors ${
              highlight
                ? "bg-[#FFEA00] text-black border-[#FFEA00]"
                : "bg-transparent text-[#FFEA00] border-[#FFEA00]"
            }`}
          >
            <Download className="w-4 h-4 stroke-[2.5]" />
            <span>Google Play</span>
          </a>
          <a
            href={APPSTORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`price-store-${id}-ios`}
            onClick={() => trackEvent("Click: App Store")}
            className={`btn-hazard relative inline-flex items-center justify-center gap-2 px-4 py-2.5 font-display font-bold text-xs tracking-widest uppercase border-2 transition-colors ${
              highlight
                ? "bg-[#FFEA00] text-black border-[#FFEA00]"
                : "bg-transparent text-[#FFEA00] border-[#FFEA00]"
            }`}
          >
            <Apple className="w-4 h-4 stroke-[2]" />
            <span>App Store</span>
          </a>
        </div>
      )}

      {/* Corner marker */}
      <div
        className={`absolute -top-px -right-px w-2.5 h-2.5 ${
          highlight ? "bg-[#FFEA00]" : "bg-white/40"
        }`}
      />
    </article>
  );
};
